import { ParsedUrlQuery } from 'querystring';
import { CheckoutSchemaFields } from 'src/schemas/CheckoutSchema';
import { OrderCreationType, OrderType, OrderUpdateType } from 'src/types';

import { formatBirthdate } from 'src/utils/date';
import { normalizePhoneFromInput } from 'src/utils/phone';

type SerializeParams = CheckoutSchemaFields & {
  contractId: string;
  queryParams: ParsedUrlQuery;
  offerId: string;
  orderId?: string;
  captchaToken: string;
  allowDocumentSwitch: boolean;
};

export const CheckoutSerializer = {
  serialize: (data: SerializeParams): OrderCreationType | OrderUpdateType => {
    const baseSerializedData: OrderCreationType | OrderUpdateType = {
      id: data.orderId,
      contractId: data.contractId,
      offerId: data.offerId,
      email: data.email,
      phone: normalizePhoneFromInput(data.phone),
      customerName: data.name,
      customerBirthdate: data.birthdate,
      customerDocument: data.cpf,
      captchaToken: data.captchaToken,
    };

    if (data.allowDocumentSwitch && data.customerType === 'cnpj') {
      baseSerializedData.customerCompanyName = data.customerCompanyName;
      baseSerializedData.customerCompanyDocument = data.customerCompanyDocument;
    }

    if (!data.orderId) baseSerializedData['queryParams'] = data.queryParams;
    if (!data.zipCode) return baseSerializedData;

    return {
      ...baseSerializedData,
      address: {
        cityId: data.cityId,
        zipcode: data.zipCode,
        street: data.street,
        neighborhood: data.neighborhood,
        number: data.number,
        complement: data.complement,
      },
    };
  },
  serializeToForm: (
    order: OrderType,
    allowDocumentSwitch: boolean,
    queryParams: ParsedUrlQuery,
  ): CheckoutSchemaFields => {
    if (!order) {
      return {
        email: (queryParams.email as string) || '',
        name: (queryParams.name as string) || '',
        cpf: (queryParams.cpf as string) || '',
        phone: (queryParams.phone as string) || '',
        birthdate: (queryParams.birthdate as string) || '',
        accept: false,
        customerType: 'cpf',
      };
    }

    let phone = order.phone.address.replace(/\D/g, '');
    if (!phone.startsWith('55')) {
      phone = '55' + phone;
    }

    const personalData: CheckoutSchemaFields = {
      name: order.customerName,
      cpf: order.customerDocument,
      birthdate: formatBirthdate(order.customerBirthdate),
      phone,
      email: order.email.address,
      accept: true,
      customerType: 'cpf',
    };

    if (allowDocumentSwitch) {
      const hasCompanyData = order.metadata?.customerCompanyName || order.metadata?.customerCompanyDocument;
      personalData.customerType = hasCompanyData ? 'cnpj' : 'cpf';
      personalData.customerCompanyName = order.metadata?.customerCompanyName;
      personalData.customerCompanyDocument = order.metadata?.customerCompanyDocument;
    }

    if (!order.address) return personalData;

    const { zipcode } = order.address;

    return {
      ...personalData,
      zipCode: `${zipcode.slice(0, 5)}-${zipcode.slice(5, 8)}`,
      number: order.address.number,
      complement: order.address.complement || '',
      cityId: order.address.city.id,
      stateId: order.address.city.state.id,
      neighborhood: order.address.neighborhood,
      street: order.address.street,
    };
  },
};
